/**
 * Unified Page Header — 4-row structure used across Shift, Procedures, Archive, Competence.
 * Breadcrumbs (Row 1) live in TopBar. This component provides Rows 2–4.
 *
 * Layout:
 *   Row 2: Title (left) | Primary Action (right) — one primary max
 *   Row 3: Search / Primary Filter (left) | Secondary Actions (right, outline buttons)
 *   Row 4: Tabs OR Filter Bar (optional, fixed slot)
 *
 * Spacing: padding 24px, row gap 12–16px, input/button height 40px.
 */
import { FC, ReactNode } from "react";

import { unifiedHeaderTokens } from "@/styles/sharedNavStyles";
import { Box } from "@mui/material";

export interface UnifiedPageHeaderProps {
	/** Row 2: Page title (left) */
	title?: ReactNode;
	/** Row 2: Primary action button (right, one max) */
	primaryAction?: ReactNode;
	/** Row 3: Search input or primary filter (left) */
	searchOrFilter?: ReactNode;
	/** Row 3: Secondary actions - outline buttons (right) */
	secondaryActions?: ReactNode;
	/** Row 4: Tabs OR Advanced filters (optional) */
	row4Content?: ReactNode;
	/** Additional sx for the header container */
	sx?: Record<string, unknown>;
	/** data-tour attribute for tour steps */
	"data-tour"?: string;
}

export const UnifiedPageHeader: FC<UnifiedPageHeaderProps> = ({
	title,
	primaryAction,
	searchOrFilter,
	secondaryActions,
	row4Content,
	sx,
	"data-tour": dataTour,
}) => {
	return (
		<Box
			data-tour={dataTour}
			sx={{
				flexShrink: 0,
				width: "100%",
				display: "flex",
				flexDirection: "column",
				gap: 0,
				py: unifiedHeaderTokens.paddingY,
				px: unifiedHeaderTokens.paddingX,
				bgcolor: "background.default",
				boxSizing: "border-box",
				...sx,
			}}
		>
			{/* Row 2: Title + Primary Action */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 0.75,
					flexWrap: "wrap",
				}}
			>
				<Box
					component="h1"
					sx={{
						m: 0,
						fontSize: unifiedHeaderTokens.titleFontSize,
						fontWeight: unifiedHeaderTokens.titleFontWeight,
						letterSpacing: "-0.02em",
						color: "text.primary",
						flexShrink: 0,
					}}
				>
					{title}
				</Box>
				{/* Always reserve right slot for alignment (primary action or empty placeholder) */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						flexShrink: 0,
						minWidth: primaryAction ? undefined : 0,
					}}
				>
					{primaryAction}
				</Box>
			</Box>

			{/* Row 3: Search/Filter (left) + Secondary Actions (right) */}
			{(searchOrFilter || secondaryActions) && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: { xs: 0.5, sm: 0.75 },
						flexWrap: "wrap",
					}}
				>
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							display: "flex",
							alignItems: "center",
							gap: { xs: 0.5, sm: 0.75 },
							flexWrap: "wrap",
						}}
					>
						{searchOrFilter}
					</Box>
					{secondaryActions && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								flexShrink: 0,
							}}
						>
							{secondaryActions}
						</Box>
					)}
				</Box>
			)}

			{/* Row 4: Tabs OR Filter Bar */}
			{row4Content && (
				<Box
					sx={{
						minHeight: 34,
						width: "100%",
						display: "flex",
						alignItems: "stretch",
					}}
				>
					{row4Content}
				</Box>
			)}
		</Box>
	);
};
